// src/pages/ModeratorCriteriasPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axios";
import "./ModeratorCriteriasPage.css";

const PAGE_SIZE = 10;

/* ===========================
   Searchable Select (no libs)
   - поиск по списку
   - закрытие по клику вне
   - закрытие по Esc
   - не ломает твои mc-* стили
=========================== */
function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  locale = "kz",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  const filtered = useMemo(() => {
    const s = String(q || "").toLowerCase().trim();
    if (!s) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(s));
  }, [options, q]);

  // click outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // ✅ закрывать селект после выбора (когда value изменился)
useEffect(() => {
  if (!open) return;
  setOpen(false);
  setQ("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [value]);


  const pick = (val) => {
    onChange(String(val));
    setOpen(false);
    setQ("");
  };

  return (
    <div className={`mc-ss ${disabled ? "mc-ss--disabled" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="mc-ss-btn"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={selected ? "mc-ss-val" : "mc-ss-ph"}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="mc-ss-caret">▾</span>
      </button>

      {open && !disabled && (
  <div
    className="mc-ss-pop"
    onMouseDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
  >
    {/* ✅ ПОИСК */}
    <input
      className="mc-ss-search"
      value={q}
      onChange={(e) => setQ(e.target.value)}
      placeholder={searchPlaceholder}
      autoFocus
    />

    <div className="mc-ss-list">
      {filtered.map((o) => (
        <button
          key={o.value}
          type="button"
          className={
            String(o.value) === String(value)
              ? "mc-ss-item mc-ss-item--active"
              : "mc-ss-item"
          }
          onMouseDown={(e) => {
  e.preventDefault();   // важно, чтобы click не терялся из-за фокуса
  e.stopPropagation();
  pick(o.value);        // setOpen(false) внутри pick
}}

          title={o.label}
        >
          {o.label}
        </button>
      ))}

      {filtered.length === 0 && (
        <div className="mc-ss-empty">
          {locale === "kz" ? "Ештеңе табылмады" : "Ничего не найдено"}
        </div>
      )}
    </div>
  </div>
)}

    </div>
  );
}

export default function ModeratorCriteriasPage({ locale = "kz" }) {
  const [criteriaCategories, setCriteriaCategories] = useState([]); // /backoffice/criteria-categories
  const [refCategories, setRefCategories] = useState([]); // /backoffice/ref/categories
  const [forms, setForms] = useState([]); // /backoffice/ref/criteria-forms
  const [criterias, setCriterias] = useState([]); // /backoffice/criterias

  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [formData, setFormData] = useState({
    name_kz: "",
    name_ru: "",
    max_point: "",
    category_id: "",
    criteria_category_id: "",
    criteria_form_id: "",
    education_level_id: null,
  });

  const t = useMemo(
    () => ({
      kz: {
        title: "Критерийлер тізімі",
        add: "Қосу",
        edit: "Өңдеу",
        save: "Сақтау",
        del: "Жою",
        empty: "Жазбалар жоқ",
        search: "Іздеу (ID, атауы, категория...)",
        req: "Барлық өрістерді толтырыңыз",
        err: "Қате орын алды",
        confirmDel: "Осы критерийді жоюға сенімдісіз бе?",
        colName: "Атауы",
        colMax: "Макс. ұпай",
        colRefCat: "Жалпы категория",
        colCritCat: "Категория",
        colAction: "Әрекет",
        modalAdd: "Критерий қосу",
        modalEdit: "Критерийді өңдеу",
        nameKz: "Атауы (қазақша)",
        nameRu: "Атауы (орысша)",
        maxPoint: "Максималды ұпай",
        pickRefCat: "Категорияны таңдаңыз",
        pickCritCat: "Категорияны таңдаңыз",
        pickForm: "Форманы таңдаңыз",
        searchPh: "Іздеу...",
        form: "Форма",
        page: "Бет",
        per: "бет",
      },
      ru: {
        title: "Список критериев",
        add: "Добавить",
        edit: "Редактировать",
        save: "Сохранить",
        del: "Удалить",
        empty: "Записей нет",
        search: "Поиск (ID, название, категория...)",
        req: "Заполните все поля",
        err: "Произошла ошибка",
        confirmDel: "Вы уверены, что хотите удалить критерий?",
        colName: "Название",
        colMax: "Макс. балл",
        colRefCat: "Категория (общая)",
        colCritCat: "Категория",
        colAction: "Действие",
        modalAdd: "Добавить критерий",
        modalEdit: "Редактирование критерия",
        nameKz: "Название (каз)",
        nameRu: "Название (рус)",
        maxPoint: "Максимальный балл",
        pickRefCat: "Выберите категорию",
        pickCritCat: "Выберите категорию",
        pickForm: "Выберите форму",
        searchPh: "Поиск...",
        form: "Форма",
        page: "Стр.",
        per: "стр.",
      },
    }),
    []
  );

  const L = locale === "kz" ? t.kz : t.ru;

  const fetchAll = async () => {
    try {
      const [critRes, critCatRes, formRes, refCatRes] = await Promise.all([
        axios.get("/backoffice/criterias"),
        axios.get("/backoffice/criteria-categories"),
        axios.get("/backoffice/ref/criteria-forms"),
        axios.get("/backoffice/ref/categories"),
      ]);

      const sorted = [...(critRes.data || [])].sort(
        (a, b) => (b?.id ?? 0) - (a?.id ?? 0)
      );

      setCriterias(sorted);
      setCriteriaCategories(Array.isArray(critCatRes.data) ? critCatRes.data : []);
      setForms(Array.isArray(formRes.data) ? formRes.data : []);
      setRefCategories(Array.isArray(refCatRes.data) ? refCatRes.data : []);
      setPage(1);
    } catch (err) {
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setFormData({
      name_kz: "",
      name_ru: "",
      max_point: "",
      category_id: "",
      criteria_category_id: "",
      criteria_form_id: "",
      education_level_id: null,
    });
    setEditingId(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const openEditModal = (criteria) => {
    setFormData({
      name_kz: criteria?.name_kz || "",
      name_ru: criteria?.name_ru || "",
      max_point: criteria?.max_point ?? "",
      category_id: criteria?.category_id ?? "",
      criteria_category_id: criteria?.criteria_category_id ?? "",
      criteria_form_id: criteria?.criteria_form_id ?? "",
      education_level_id: criteria?.education_level_id ?? null,
    });
    setEditingId(criteria?.id);
    setIsEditing(true);
    setModalOpen(true);
  };

  const refCatById = useMemo(() => {
    const m = new Map();
    refCategories.forEach((x) => m.set(Number(x.id), x));
    return m;
  }, [refCategories]);

  const critCatById = useMemo(() => {
    const m = new Map();
    criteriaCategories.forEach((x) => m.set(Number(x.id), x));
    return m;
  }, [criteriaCategories]);

  const formById = useMemo(() => {
    const m = new Map();
    forms.forEach((x) => m.set(Number(x.id), x));
    return m;
  }, [forms]);

  // options for searchable selects
  const refCategoryOptions = useMemo(
    () =>
      refCategories.map((c) => ({
        value: c.id,
        label: locale === "kz" ? c.name_kz : c.name_ru,
      })),
    [refCategories, locale]
  );

  const criteriaCategoryOptions = useMemo(
    () =>
      criteriaCategories.map((c) => ({
        value: c.id,
        label: locale === "kz" ? c.name_kz : c.name_ru,
      })),
    [criteriaCategories, locale]
  );

  const formOptions = useMemo(
    () => forms.map((f) => ({ value: f.id, label: f.name })),
    [forms]
  );

  const filteredCriterias = useMemo(() => {
    const s = String(q || "").toLowerCase().trim();
    if (!s) return criterias;

    return criterias.filter((c) => {
      const id = String(c?.id ?? "");
      const nkz = String(c?.name_kz || "").toLowerCase();
      const nru = String(c?.name_ru || "").toLowerCase();

      const refCat = refCatById.get(Number(c?.category_id));
      const refCatKz = String(refCat?.name_kz || "").toLowerCase();
      const refCatRu = String(refCat?.name_ru || "").toLowerCase();

      const critCat = critCatById.get(Number(c?.criteria_category_id));
      const critCatKz = String(critCat?.name_kz || "").toLowerCase();
      const critCatRu = String(critCat?.name_ru || "").toLowerCase();

      const form = formById.get(Number(c?.criteria_form_id));
      const formName = String(form?.name || "").toLowerCase();

      return (
        id.includes(s) ||
        nkz.includes(s) ||
        nru.includes(s) ||
        refCatKz.includes(s) ||
        refCatRu.includes(s) ||
        critCatKz.includes(s) ||
        critCatRu.includes(s) ||
        formName.includes(s)
      );
    });
  }, [q, criterias, refCatById, critCatById, formById]);

  const totalPages = Math.max(1, Math.ceil(filteredCriterias.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const currentItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredCriterias.slice(start, start + PAGE_SIZE);
  }, [filteredCriterias, page]);

  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const buildPayload = () => {
    const {
      name_kz,
      name_ru,
      max_point,
      category_id,
      criteria_category_id,
      criteria_form_id,
      education_level_id,
    } = formData;

    const requiredOk =
      name_kz?.trim() &&
      name_ru?.trim() &&
      String(max_point).trim() !== "" &&
      String(category_id).trim() !== "" &&
      String(criteria_category_id).trim() !== "" &&
      String(criteria_form_id).trim() !== "";

    if (!requiredOk) return { ok: false, payload: null };

    return {
      ok: true,
      payload: {
        name_kz: name_kz.trim(),
        name_ru: name_ru.trim(),
        max_point: Number(max_point),
        category_id: Number(category_id),
        criteria_category_id: Number(criteria_category_id),
        criteria_form_id: Number(criteria_form_id),
        education_level_id:
          education_level_id === "" || education_level_id === null
            ? null
            : Number(education_level_id),
      },
    };
  };

  const handleSubmit = async () => {
    const { ok, payload } = buildPayload();
    if (!ok) {
      alert(L.req);
      return;
    }

    try {
      if (isEditing) {
        await axios.put(`/backoffice/criterias/${editingId}`, payload);
      } else {
        await axios.post("/backoffice/criterias", payload);
      }
      await fetchAll();
      setModalOpen(false);
      setPage(1);
    } catch (err) {
      alert(err?.response?.data?.message || L.err);
    }
  };

  const handleDelete = async () => {
    if (!isEditing || !editingId) return;
    const ok = window.confirm(L.confirmDel);
    if (!ok) return;

    try {
      await axios.delete(`/backoffice/criterias/${editingId}`);
      await fetchAll();
      setModalOpen(false);
      setPage(1);
    } catch (err) {
      alert(err?.response?.data?.message || L.err);
    }
  };

  return (
    <div className="mc-page">
      <div className="mc-head">
        <h2>🧮 {L.title}</h2>
        <button className="mc-btn mc-btn-primary" onClick={openAddModal} type="button">
          ➕ {L.add}
        </button>
      </div>

      <div className="mc-toolbar">
        <input
          className="mc-search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder={L.search}
        />
        {!!q && (
          <button
            className="mc-clear"
            type="button"
            onClick={() => {
              setQ("");
              setPage(1);
            }}
          >
            ✕
          </button>
        )}
      </div>

      <div className="mc-table-wrap">
        <table className="mc-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>{L.colName}</th>
              <th>{L.colMax}</th>
              <th>{L.colRefCat}</th>
              <th>{L.colCritCat}</th>
              <th>{L.colAction}</th>
            </tr>
          </thead>

          <tbody>
            {currentItems.map((c) => (
              <tr key={c.id}>
                <td>{c.id}</td>
                <td>{locale === "kz" ? c.name_kz : c.name_ru}</td>
                <td>{c.max_point}</td>
                <td>
                  {(() => {
                    const cat = refCatById.get(Number(c?.category_id));
                    return cat ? (locale === "kz" ? cat.name_kz : cat.name_ru) : "-";
                  })()}
                </td>
                <td>
                  {(() => {
                    const cat = critCatById.get(Number(c?.criteria_category_id));
                    return cat ? (locale === "kz" ? cat.name_kz : cat.name_ru) : "-";
                  })()}
                </td>
                <td>
                  <button
                    className="mc-btn mc-btn-ghost mc-btn-sm"
                    type="button"
                    onClick={() => openEditModal(c)}
                  >
                    ✏️ {L.edit}
                  </button>
                </td>
              </tr>
            ))}

            {currentItems.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 20, color: "#6b7280" }}>
                  {L.empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mc-pager">
        <button className="mc-pager-btn" onClick={() => goTo(1)} disabled={page === 1}>
          ⏮
        </button>
        <button className="mc-pager-btn" onClick={() => goTo(page - 1)} disabled={page === 1}>
          ‹
        </button>
        <span className="mc-pager-info">
          {L.page} {page}/{totalPages}
          <span className="mc-pager-sep"> • </span>
          {PAGE_SIZE} / {L.per}
        </span>
        <button
          className="mc-pager-btn"
          onClick={() => goTo(page + 1)}
          disabled={page === totalPages}
        >
          ›
        </button>
        <button
          className="mc-pager-btn"
          onClick={() => goTo(totalPages)}
          disabled={page === totalPages}
        >
          ⏭
        </button>
      </div>

      {modalOpen && (
        <div
          className="mc-backdrop"
          onClick={(e) => {
            if (e.target.classList.contains("mc-backdrop")) setModalOpen(false);
          }}
        >
          <div
            className="mc-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mc-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="mc-modal-title" className="mc-title">
              {isEditing ? L.modalEdit : L.modalAdd}
            </h3>

            <div className="mc-form-grid">
              <label className="mc-field">
                <span className="mc-label">{L.nameKz}</span>
                <input
                  type="text"
                  value={formData.name_kz}
                  onChange={(e) => setFormData({ ...formData, name_kz: e.target.value })}
                  required
                />
                <small className="mc-hint">
                  {locale === "kz"
                    ? "Критерий атауы қазақ тілінде"
                    : "Название критерия на казахском"}
                </small>
              </label>

              <label className="mc-field">
                <span className="mc-label">{L.nameRu}</span>
                <input
                  type="text"
                  value={formData.name_ru}
                  onChange={(e) => setFormData({ ...formData, name_ru: e.target.value })}
                  required
                />
                <small className="mc-hint">
                  {locale === "kz" ? "Критерий атауы орыс тілінде" : "Название критерия на русском"}
                </small>
              </label>

              <label className="mc-field mc-field--compact">
                <span className="mc-label">{L.maxPoint}</span>
                <input
                  type="number"
                  min="0"
                  value={formData.max_point}
                  onChange={(e) => setFormData({ ...formData, max_point: e.target.value })}
                  required
                />
                <small className="mc-hint">{locale === "kz" ? "Мысалы: 1, 2, 3" : "Например: 1, 2, 3"}</small>
              </label>

              {/* ✅ searchable select: ref/categories */}
              <label className="mc-field">
                <span className="mc-label">{L.colRefCat}</span>
                <SearchSelect
                  locale={locale}
                  value={formData.category_id}
                  onChange={(val) => setFormData({ ...formData, category_id: val })}
                  options={refCategoryOptions}
                  placeholder={L.pickRefCat}
                  searchPlaceholder={L.searchPh}
                  disabled={refCategoryOptions.length === 0}
                />
                <small className="mc-hint">
                  {locale === "kz"
                    ? "Бұл — ref/categories ішіндегі категория"
                    : "Это категория из справочника ref/categories"}
                </small>
              </label>

              {/* ✅ searchable select: criteria-categories */}
              <label className="mc-field">
                <span className="mc-label">{L.colCritCat}</span>
                <SearchSelect
                  locale={locale}
                  value={formData.criteria_category_id}
                  onChange={(val) => setFormData({ ...formData, criteria_category_id: val })}
                  options={criteriaCategoryOptions}
                  placeholder={L.pickCritCat}
                  searchPlaceholder={L.searchPh}
                  disabled={criteriaCategoryOptions.length === 0}
                />
                <small className="mc-hint">
                  {locale === "kz"
                    ? "Критерий қай санатқа жатады"
                    : "К какому разделу относится критерий"}
                </small>
              </label>

              {/* ✅ searchable select: forms */}
              <label className="mc-field">
                <span className="mc-label">{L.form}</span>
                <SearchSelect
                  locale={locale}
                  value={formData.criteria_form_id}
                  onChange={(val) => setFormData({ ...formData, criteria_form_id: val })}
                  options={formOptions}
                  placeholder={L.pickForm}
                  searchPlaceholder={L.searchPh}
                  disabled={formOptions.length === 0}
                />
                <small className="mc-hint">
                  {locale === "kz"
                    ? "Бағалау форматы (мысалы, баспа / цифрлық)"
                    : "Формат оценивания (например, печатная / цифровая)"}
                </small>
              </label>
            </div>

            <div className="mc-actions">
              {isEditing && (
                <button type="button" onClick={handleDelete} className="mc-btn mc-btn-danger">
                  🗑 {L.del}
                </button>
              )}
              <button type="button" onClick={handleSubmit} className="mc-btn mc-btn-primary">
                💾 {L.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
